import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  inject,
  output,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EllipsisDirective } from '../../../directives/ellipsis/ellipsis';
import { HighlightsService } from '../../../services/selections/highlights';
import { SelectionsService } from '../../../services/selections/selections';
import { ToastService } from '../../../services/toast/toast';

@Component({
  selector: 'nr-note-dialog',
  imports: [TranslatePipe, EllipsisDirective, ReactiveFormsModule],
  templateUrl: './note-dialog.html',
  styleUrl: '../notes.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class NoteDialogComponent {
  private readonly highlightsService = inject(HighlightsService);
  private readonly selectionsService = inject(SelectionsService);
  private readonly translateService = inject(TranslateService);
  private readonly toastService = inject(ToastService);

  readonly note = this.highlightsService.noteToEdit;
  readonly shareNote = this.highlightsService.shareNote;
  readonly noteControl = new FormControl<string>('');
  readonly showMoreActions = signal(true);

  readonly colorMap = this.highlightsService.colorMap;

  readonly dialogClosed = output();

  constructor() {
    effect(() => {
      this.noteControl.setValue(this.note()?.notes ?? '', { emitEvent: false });
    });
  }

  handleCopySelectedText(): void {
    const note = this.note();
    if (!note) return;
    navigator.clipboard.writeText(note.selectedText);
    this.toastService.success(String(this.translateService.instant('COPIED_TO_CLIPBOARD')));
  }

  removeNote(): void {
    const note = this.note();
    if (!note) return;
    this.selectionsService.removeSelection(note.id);
    this.highlightsService.removeHighlight(note);
  }

  async updateNote(): Promise<void> {
    const note = this.note();
    if (!note) return;
    const updatedNote = { ...note, notes: this.noteControl.value ?? '' };
    await this.selectionsService.updateSelection(note.id, updatedNote);
    this.highlightsService.noteIsDraft.set(false);
    this.highlightsService.noteToEdit.set(undefined);
  }
}
